import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { Modal } from '../../elements/Modal';
import ReasonScreen from './cancelFlow/ReasonScreen';
import FinalScreen from './cancelFlow/FinalScreen';
import ExpensiveScreen from './cancelFlow/ExpensiveScreen';
import TooMuchScreen from './cancelFlow/TooMuchScreen';
import ChipFeedbackScreen from './cancelFlow/ChipFeedbackScreen';
import ResultsScreen from './cancelFlow/ResultsScreen';
import OtherScreen from './cancelFlow/OtherScreen';
import { FORMAT_CHIPS, TASTE_CHIPS } from './cancelFlow/constants';
import {
  useApplyCouponToSubscriptionMutation,
  useRequestFormulaReviewMutation,
  useRestartSubscriptionMutation,
  useGetDashboardSummaryQuery,
} from '../../../features/customerApiSlice';

function CancelSubscriptionModal({
  open,
  onClose,
  stripeSubscriptionId,
  userSubscriptionId,
  productType,
  lang,
  subscriptionAmount,
  minPauseDate,
  optionDates,
  onConfirmCancel,
  onConfirmPause,
  refetchUserSubscription,
  isSubmitting,
  canApplyCoupon = true,
  canRequestFormulaReview = true,
}) {
  const { t } = useTranslation();
  const { currentData: dashboardSummary, isFetching: isGiftStatusFetching } = useGetDashboardSummaryQuery(
    { lang },
    { skip: !open || productType !== 'riise_formula', refetchOnMountOrArgChange: 30 },
  );
  const gift = dashboardSummary?.gift;
  const hasPendingGift = Boolean(
    open && productType === 'riise_formula' && !isGiftStatusFetching &&
    gift && ['teaser', 'unlocked', 'reserved'].includes(gift.status) &&
    Number(gift.userSubscriptionId) === Number(userSubscriptionId),
  );
  const [screen, setScreen] = useState('reason'); // 'reason' | 'expensive' | 'toomuch' | 'taste' | 'format' | 'results' | 'other' | 'final'
  const [selectedReason, setSelectedReason] = useState(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackChips, setFeedbackChips] = useState([]);
  const [toomuchOption, setToomuchOption] = useState(null); // 'skip_1' | 'skip_2' | 'date'
  const [pauseDate, setPauseDate] = useState('');
  const [finalState, setFinalState] = useState('cancelled'); // 'cancelled' | 'saved' | 'restarted'
  const [finalMessage, setFinalMessage] = useState({ title: '', body: '' });
  const [confirmAction, setConfirmAction] = useState(null); // 'cancel' | 'pause' | 'discount' | 'restart'
  const [isConfirmSubmitting, setIsConfirmSubmitting] = useState(false);

  const [restartSubscriptionMutation, { isLoading: isRestarting }] = useRestartSubscriptionMutation();
  const [applyCouponToSubscriptionMutation, { isLoading: isApplyingCoupon }] =
    useApplyCouponToSubscriptionMutation();
  const [requestFormulaReviewMutation, { isLoading: isRequestingReview }] = useRequestFormulaReviewMutation();

  const formattedPauseDate =
    pauseDate && !Number.isNaN(new Date(pauseDate + 'T00:00:00').getTime())
      ? new Date(pauseDate + 'T00:00:00').toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        })
      : null;

  useEffect(() => {
    if (open) {
      setScreen('reason');
      setSelectedReason(null);
      setFeedbackText('');
      setFeedbackChips([]);
      setToomuchOption(null);
      setPauseDate('');
      setFinalState('cancelled');
      setFinalMessage({ title: '', body: '' });
    }
  }, [open]);

  const handleClose = () => {
    onClose();
  };

  const goToStep2 = () => {
    if (!selectedReason) return;
    setScreen(selectedReason); // 'expensive' | 'toomuch' | 'taste' | 'format' | 'results' | 'other'
  };

  const handleDirectCancel = async () => {
    setConfirmAction('cancel');
  };

  const handleDiscount = async () => {
    if (!stripeSubscriptionId) return;
    if (!canApplyCoupon) {
      toast.error(t('CancelFlow_Expensive_Unavailable'));
      return;
    }
    setConfirmAction('discount');
  };

  const handleTooMuchConfirm = async () => {
    if (!toomuchOption) return;
    setConfirmAction('pause');
  };

  const handleBookReviewFlow = async () => {
    const note = feedbackText.trim();
    if (!canRequestFormulaReview) {
      toast.error(t('CustomerSubs_NoActiveSubToCancel'));
      return;
    }
    if (!note) {
      toast.error(t('CancelFlow_Results_ReviewNote_Required'));
      return;
    }

    await requestFormulaReviewMutation({
      note,
      userSubscriptionId,
      product_type: productType,
    }).unwrap();
    setFinalState('saved');
    setFinalMessage({
      title: t('CancelFlow_FinalMessage_ReviewBooked_Title'),
      body: t('CancelFlow_FinalMessage_ReviewBooked_Body'),
    });
    setScreen('final');
    await refetchUserSubscription?.();
  };

  const handleRestart = () => {
    setConfirmAction('restart');
  };

  let content = null;

  if (screen === 'reason') {
    content = (
      <ReasonScreen
        selectedReason={selectedReason}
        onSelect={setSelectedReason}
        onNext={goToStep2}
        onClose={handleClose}
      />
    );
  } else if (screen === 'expensive') {
    content = (
      <ExpensiveScreen
        onBack={() => setScreen('reason')}
        onClose={handleClose}
        onClaimDiscount={handleDiscount}
        onCancelAnyway={handleDirectCancel}
        isSubmitting={isSubmitting}
        subscriptionAmount={subscriptionAmount}
        canClaimDiscount={canApplyCoupon}
      />
    );
  } else if (screen === 'toomuch') {
    content = (
      <TooMuchScreen
        onBack={() => setScreen('reason')}
        onClose={handleClose}
        option={toomuchOption}
        onSelectOption={setToomuchOption}
        pauseDate={pauseDate}
        onDateChange={setPauseDate}
        minDate={minPauseDate}
        optionDates={optionDates}
        onConfirm={handleTooMuchConfirm}
        onCancelAnyway={handleDirectCancel}
        isSubmitting={isSubmitting}
      />
    );
  } else if (screen === 'taste' || screen === 'format' || screen === 'results' || screen === 'other') {
    if (screen === 'taste' || screen === 'format') {
      const isTaste = screen === 'taste';
      content = (
        <ChipFeedbackScreen
          step={isTaste ? 'taste' : 'format'}
          stepTitleHtml={
            isTaste
              ? `${t('CancelFlow_Taste_Title_Line1')}<br/><em>${t('CancelFlow_Taste_Title_Line2')}</em>`
              : `${t('CancelFlow_Format_Title_Line1')}<br/><em>${t('CancelFlow_Format_Title_Line2')}</em>`
          }
          stepSubtitle={isTaste ? t('CancelFlow_Taste_Subtitle') : t('CancelFlow_Format_Subtitle')}
          chips={isTaste ? TASTE_CHIPS : FORMAT_CHIPS}
          feedbackChips={feedbackChips}
          onChangeChips={setFeedbackChips}
          feedbackText={feedbackText}
          onChangeText={setFeedbackText}
          onConfirm={handleDirectCancel}
          onBack={() => setScreen('reason')}
          onClose={handleClose}
          isSubmitting={isSubmitting}
        />
      );
    } else if (screen === 'results') {
      content = (
        <ResultsScreen
          onBack={() => setScreen('reason')}
          onBookReview={handleBookReviewFlow}
          onCancelAnyway={handleDirectCancel}
          onClose={handleClose}
          isSubmitting={isSubmitting || isRequestingReview}
          reviewNote={feedbackText}
          onChangeReviewNote={setFeedbackText}
          canRequestReview={canRequestFormulaReview}
        />
      );
    } else if (screen === 'other') {
      content = (
        <OtherScreen
          onBack={() => setScreen('reason')}
          feedbackText={feedbackText}
          onChangeText={setFeedbackText}
          onConfirm={handleDirectCancel}
          onClose={handleClose}
          isSubmitting={isSubmitting}
        />
      );
    }
  } else if (screen === 'final') {
    content = (
      <FinalScreen
        state={finalState}
        message={finalMessage}
        onClose={handleClose}
        onRestart={handleRestart}
      />
    );
  }

  const handleConfirmAction = async () => {
    if (!confirmAction || isConfirmSubmitting) return;

    setIsConfirmSubmitting(true);

    try {
      if (confirmAction === 'cancel') {
        await onConfirmCancel?.({
          reason: selectedReason,
          extraNote: feedbackText,
          tags: feedbackChips.join(', '),
        });
        setFinalState('cancelled');
        setConfirmAction(null);
        setScreen('final');
        await refetchUserSubscription?.();
      } else if (confirmAction === 'pause') {
        await onConfirmPause?.({ option: toomuchOption, date: pauseDate || null });

        if (toomuchOption === 'skip_1' || toomuchOption === 'skip_2') {
          setFinalState('saved');
          setFinalMessage({
            title: t('CancelFlow_FinalMessage_Skip_Title'),
            body: t('CancelFlow_FinalMessage_Skip_Body'),
          });
        } else {
          setFinalState('saved');
          setFinalMessage({
            title: t('CancelFlow_FinalMessage_Pause_Title'),
            body: t('CancelFlow_FinalMessage_Pause_Body'),
          });
        }
        setConfirmAction(null);
        setScreen('final');
        await refetchUserSubscription?.();
      } else if (confirmAction === 'discount') {
        if (!stripeSubscriptionId) return;
        if (!canApplyCoupon) {
          toast.error(t('CancelFlow_Expensive_Unavailable'));
          return;
        }
        await applyCouponToSubscriptionMutation({ stripeSubscriptionId, userSubscriptionId, product_type: productType }).unwrap();
        setFinalState('saved');
        setFinalMessage({
          title: t('CancelFlow_FinalMessage_Discount_Title'),
          body: t('CancelFlow_FinalMessage_Discount_Body'),
        });
        setConfirmAction(null);
        setScreen('final');
        await refetchUserSubscription?.();
      } else if (confirmAction === 'restart') {
        if (!stripeSubscriptionId) return;
        await restartSubscriptionMutation({ stripeSubscriptionId, userSubscriptionId, product_type: productType }).unwrap();
        setFinalState('restarted');
        setConfirmAction(null);
        setScreen('final');
        await refetchUserSubscription?.();
      }
    } catch (e) {
      const message = e?.data?.message || e?.message || 'Something went wrong!';
      toast.error(message);
    } finally {
      setIsConfirmSubmitting(false);
    }
  };

  const isConfirmBusy =
    isConfirmSubmitting || isSubmitting || isRestarting || isApplyingCoupon || isRequestingReview;

  return (
    <>
      <Modal
        open={open}
        handleClose={handleClose}
        className="t-max-w-[95vw] lg:t-max-w-[480px] !t-rounded-2xl !t-px-0"
        childrenClass="t-items-stretch"
        notCrossIcon
        enableClickOutsideClose={false}
      >
        <div className="t-w-full">
          <div className="t-px-6 t-pb-7 t-pt-4">{content}</div>
        </div>
      </Modal>

      <Modal
        open={!!confirmAction}
        handleClose={() => {
          if (isConfirmBusy) return;
          setConfirmAction(null);
        }}
        enableClickOutsideClose={false}
        className="!t-max-w-[95vw] lg:t-max-w-[400px]"
      >
        <div className="t-p-6 t-flex t-flex-col t-gap-4 t-max-w-sm t-w-full">
          <h2 className="t-text-lg t-font-semibold t-text-center t-m-0">
            {confirmAction === 'cancel' && t('CancelFlow_Confirm_Title_Cancel')}
            {confirmAction === 'pause' && t('CancelFlow_Confirm_Title_Pause')}
            {confirmAction === 'discount' && t('CancelFlow_Confirm_Title_Discount')}
            {confirmAction === 'restart' && t('CancelFlow_Confirm_Title_Restart')}
          </h2>
          <p className="t-text-sm t-text-slate-600 t-text-center t-m-0">
            {confirmAction === 'cancel' && t('CancelFlow_Confirm_Cancel')}
            {confirmAction === 'pause' &&
              (toomuchOption === 'date' && formattedPauseDate
                ? t('CancelFlow_Confirm_PauseUntil', { date: formattedPauseDate })
                : t('CancelFlow_Confirm_PauseSkip'))}
            {confirmAction === 'discount' && t('CancelFlow_Confirm_Discount')}
            {confirmAction === 'restart' && t('CancelFlow_Confirm_Restart')}
          </p>
          {confirmAction === 'cancel' && hasPendingGift && (
            <p className="t-m-0 t-rounded-lg t-bg-butter/30 t-p-3 t-text-center t-text-sm t-text-navy">
              {t(gift.status === 'reserved' ? 'RetentionGift_CancelReserved' : 'RetentionGift_CancelOffer')}
            </p>
          )}
          <div className="t-flex t-flex-col sm:t-flex-row t-items-stretch sm:t-items-center t-gap-3 t-mt-2">
            <button
              type="button"
              onClick={handleConfirmAction}
              className="t-flex-1 t-inline-flex t-items-center t-justify-center t-rounded-lg t-px-4 t-py-2 t-text-sm t-font-semibold t-bg-secondary t-text-white hover:t-bg-teal-800 disabled:t-opacity-60"
              disabled={isConfirmBusy || (confirmAction === 'pause' && !toomuchOption)}
            >
              {t('CancelFlow_Confirm_Yes')}
            </button>
            <button
              type="button"
              onClick={() => {
                if (isConfirmBusy) return;
                setConfirmAction(null);
              }}
              className="t-flex-1 t-inline-flex t-items-center t-justify-center t-rounded-lg t-px-4 t-py-2 t-text-sm t-font-semibold t-bg-slate-100 t-text-slate-700 hover:t-bg-slate-200"
              disabled={isConfirmBusy}
            >
              {t('CancelFlow_Confirm_No')}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}

export default CancelSubscriptionModal;
